<template>
  <div class="vehicle-details">
    <div class="vehicle-details__header mb-6">
      <PageComponentsCarDetailTitle />
    </div>

    <div class="vehicle-details__main-image-slider block" v-if="vehicleImages.length">
      <div class="vehicle-details__main-image-slider-item min-h-[250px]">
        <SharedPicture
          :media="vehicleImages[0]"
          fetchpriority="high"
          loading="eager"
          :class="{ hidden: mainSliderInitialised, 'block sm:!hidden': !mainSliderInitialised }"
          imageClass="w-full h-full overflow-hidden !object-contain object-center rounded-sm"
          class="h-full !aspect-[303/228] w-full !object-contain object-center"
          height="483"
          width="642"
          format="webp"
          :quality="85"
        />
        <SharedPicture
          :media="vehicleImages[0]"
          fetchpriority="high"
          loading="eager"
          :class="{ hidden: mainSliderInitialised, 'block sm:!hidden': !mainSliderInitialised }"
          imageClass="w-full h-full overflow-hidden !object-contain object-center rounded-sm"
          class="h-full !aspect-[303/228] w-full !object-contain object-center"
          height="570"
          width="757"
          format="webp"
          :quality="85"
        />
        <client-only>
          <swiper-container
            :class="{ hidden: !mainSliderInitialised }"
            class="h-full overflow-hidden w-full block print:h-full"
            space-between="30"
            @swiperactiveindexchange="handleMainSlideChange"
            @swiperinit="handleMainSliderInit"
          >
            <swiper-slide v-for="(image, i) in vehicleImages" :key="image?.url || i" :lazy="i > 4" class="min-h-[250px]" @click="openLightbox(i)">
              <SharedPicture
                :fetchpriority="i < 4 ? 'high' : 'low'"
                :loading="i === 0 ? 'eager' : 'lazy'"
                :media="image"
                imageClass="w-full h-full overflow-hidden !object-contain object-center rounded-sm"
                class="h-full !aspect-[303/228] w-full !object-contain object-center block print:h-full sm:!hidden"
                height="483"
                width="642"
                format="webp"
                :quality="85"
              />
              <SharedPicture
                :fetchpriority="i < 4 ? 'high' : 'low'"
                :loading="i === 0 ? 'eager' : 'lazy'"
                :media="image"
                imageClass="w-full h-full overflow-hidden !object-contain object-center rounded-sm"
                class="h-full !aspect-[303/228] w-full !object-contain object-center !hidden sm:!block print:h-full"
                height="570"
                width="757"
                format="webp"
                :quality="85"
              />
            </swiper-slide>
          </swiper-container>
        </client-only>
      </div>

      <client-only>
        <VueEasyLightbox v-if="lightboxVisible" :imgs="lightboxImages" :index="lightboxIndex" :visible="lightboxVisible" @hide="onLightboxHide" teleport="#floating-actions-container" />
      </client-only>

      <div class="vehicle-details__main-image-slider-navigation mt-4 min-h-[75px] relative">
        <div class="flex gap-2 items-center w-max h-full absolute top-0 left-0" :class="{ hidden: secondarySliderInitialised }">
          <SharedPicture
            v-for="(image, i) in vehicleImages.slice(0, 5)"
            :fetchpriority="i < 4 ? 'high' : 'low'"
            :loading="i < 4 ? 'eager' : 'lazy'"
            :media="image"
            imageClass="w-full h-full !aspect-[303/228] overflow-hidden !object-contain object-center rounded-sm block"
            :class="{ 'border-accent/100': activeIndex === i }"
            class="h-full w-auto !aspect-[303/228] !object-contain object-center block border border-accent/0 rounded-sm"
            height="75"
            width="101"
            format="webp"
            :quality="85"
          />
        </div>
        <client-only>
          <swiper-container
            @swiperinit="handleSecondarySliderInit"
            space-between="8"
            class="w-screen h-full absolute top-0 left-0"
            :class="{ hidden: !secondarySliderInitialised }"
            :slides-per-view="'auto'"
            :slides-offset-after="38"
            :active-index="activeIndex"
          >
            <swiper-slide v-for="(image, i) in vehicleImages" :key="image?.url || i" :lazy="i > 6" class="w-auto h-full" @click="changeMainSlide(i)">
              <SharedPicture
                :fetchpriority="i < 6 ? 'high' : 'low'"
                :loading="i < 6 ? 'eager' : 'lazy'"
                :media="image"
                imageClass="w-full h-full overflow-hidden !object-contain object-center rounded-sm"
                :class="{ 'border-accent/100': activeIndex === i }"
                class="h-full w-auto !aspect-[303/228] !object-contain object-center block border border-accent/0 rounded-sm"
                height="75"
                width="101"
                format="webp"
                :quality="75"
              />
            </swiper-slide>
          </swiper-container>
        </client-only>
      </div>
    </div>

    <PageComponentsCarDetailBreadCrumbs class="mt-6 mb-8" />

    <div class="vehicle-details__title-data flex flex-col gap-4">
      <div class="flex items-center justify-between">
        <PageComponentsCarDetailID />
        <PageComponentsCarDetailPrice />
      </div>

      <div class="flex items-center justify-center">
        <PageComponentsCarDetailAutoUncleIframe />
      </div>

      <PageComponentsCarDetailFeaturesList />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import type { GraphQLCarDetail } from '~/types/graphql';
  import type { Swiper } from 'swiper/types';

  const carDetail = inject<GraphQLCarDetail>('carDetail');
  const vehicleImages = computed(() => useVehicleImages(carDetail ?? {}));

  const mainSliderInitialised = ref(false);
  const activeIndex = ref(0);

  const mainSlider = ref<Swiper | null>(null);

  const handleMainSliderInit = (e: CustomEvent<Swiper[]>) => {
    if (e.detail[0]) {
      mainSlider.value = e.detail[0];
      mainSliderInitialised.value = true;
    }
  };

  const handleMainSlideChange = (e: CustomEvent<Swiper[]>) => {
    if (e.detail[0]) {
      const swiper = e.detail[0];
      activeIndex.value = swiper.activeIndex;
    }
  };

  const changeMainSlide = (index: number) => {
    if (mainSlider.value) {
      mainSlider.value.slideTo(index);
    }
  };

  const secondarySliderInitialised = ref(false);
  const secondarySlider = ref<Swiper | null>(null);

  const handleSecondarySliderInit = (e: CustomEvent<Swiper[]>) => {
    if (e.detail[0]) {
      secondarySlider.value = e.detail[0];
      secondarySliderInitialised.value = true;
    }
  };

  watch(activeIndex, newIndex => {
    if (secondarySlider.value) {
      secondarySlider.value.slideTo(newIndex);
    }
  });

  // Lazy-load the lightbox — its chunk (JS + CSS) is fetched on first open, not on page load.
  const VueEasyLightbox = defineAsyncComponent(() => import('vue-easy-lightbox'));

  const lightboxIndex = ref(0);
  const lightboxVisible = ref(false);

  const lightboxImages = ref<string[]>([]);

  const { lockScroll, unlockScroll } = useScrollLock();

  const config = useRuntimeConfig();

  const openLightbox = (index: number) => {
    if (!vehicleImages.value.length) return;

    // zero out the images array
    const images = <string[]>[];

    vehicleImages.value.forEach(image => {
      const imgSrc = new URL(image.url, config.public.strapi.url).toString();
      if (imgSrc) {
        images.push(imgSrc);
      }
    });

    lightboxImages.value = images;

    lockScroll();
    lightboxIndex.value = index;
    lightboxVisible.value = true;
  };

  const onLightboxHide = () => {
    lightboxVisible.value = false;
    unlockScroll();
  };
</script>

<style lang="scss" scoped>
  .vehicle-details {
    &__title {
      font-family: Chivo;
      font-size: 32px;
      font-style: normal;
      font-weight: 600;
      line-height: 120%; /* 38.4px */
      letter-spacing: -0.32px;
    }
  }
</style>
